'use client';

import { useSearchParams } from "next/navigation";
import { format } from "date-fns";
import apiService from "@/app/services/apiService";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import ProductListItem from "./ProductListItem";
import useSearchModal from "@/app/hooks/useSearchModal";

export type ProductType = {
    id: string;
    title: string;
    price_per_night: number;
    image_url: string;
    is_favorite: boolean;
    vendor_id?: string;
}

interface ProductListProps {
    vendor_id?: string | null;
    favorites?: boolean | null;
    isManageMode?: boolean;
}

const EMPTY_IMAGES = [
    "/images/sponsors/apoio-local1.png",
    "/images/sponsors/apoio-local4.png",
    "/images/sponsors/brahma-seeklogo.png",
    "/images/sponsors/apoio-local7.png",
    "/images/sponsors/apoio-local5.png",
    "/images/sponsors/nubank-white.png",
];

const ProductList: React.FC<ProductListProps> = ({
    vendor_id,
    favorites,
    isManageMode = false
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [products, setProducts] = useState<ProductType[]>([]);
    const [loading, setLoading] = useState(true);

    const params = useSearchParams();
    const searchModal = useSearchModal();
    const { country, days: numDays, checkIn: checkinDate, checkOut: checkoutDate, category } = searchModal.query;

    // Carrossel de imagens vazias mudado de 3s fixos conforme o comentário corrigido
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % EMPTY_IMAGES.length);
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    // Otimizado com useCallback para evitar loops de renderização no useEffect
    const markFavorite = useCallback((id: string, is_favorite: boolean) => {
        setProducts((prevProducts) =>
            prevProducts.map((product) =>
                product.id === id ? { ...product, is_favorite } : product
            )
        );
    }, []);

    const getProducts = useCallback(async () => {
        setLoading(true);
        try {
            let url = '/api/products/';
            const urlParams = new URLSearchParams();

            if (vendor_id) {
                urlParams.append('vendor_id', vendor_id);
            } else if (favorites) {
                urlParams.append('is_favorites', 'true');
            } else {
                if (country) urlParams.append('country', country);
                if (numDays) urlParams.append('numDays', numDays.toString());
                if (category) urlParams.append('category', category);
                
                if (checkinDate) {
                    urlParams.append('checkin', format(new Date(checkinDate), 'yyyy-MM-dd'));
                }
                if (checkoutDate) {
                    urlParams.append('checkout', format(new Date(checkoutDate), 'yyyy-MM-dd'));
                }
            }

            const queryString = urlParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            console.log("🔎 GET URL:", url);
            const response = await apiService.get(url);
            console.log("📦 Resposta da API:", response);

            // Resguarda a renderização mapeando os favoritos corretamente
            const mappedProducts = (response.data || []).map((product: ProductType) => {
                let isFav = false;
                if (response.favorites) {
                    isFav = response.favorites.includes(product.id);
                } else if (favorites) {
                    isFav = true;
                }
                return { ...product, is_favorite: isFav };
            });

            // 🌟 DUPLA SEGURANÇA VISUAL (FILTRO FRONT-END CLIENT-SIDE)
            // Se o endpoint por acaso ignorar a query do vendor_id e retornar dados mistos,
            // barramos aqui para garantir que nada de outros vendors renderize na tela.
            const finalProducts = vendor_id && mappedProducts.length > 0 && 'vendor_id' in mappedProducts[0]
              ? mappedProducts.filter((p: any) => String(p.vendor_id) === String(vendor_id))
              : mappedProducts;

            setProducts(finalProducts);
        } catch (error) {
            console.error("Erro ao buscar produtos:", error);
        } finally {
            setLoading(false);
        }
    }, [vendor_id, favorites, country, numDays, category, checkinDate, checkoutDate]);

    useEffect(() => {
        getProducts();
    }, [getProducts, category, params]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[300px]">
                <p className="text-gray-500 font-medium">Carregando...</p>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className="col-span-full flex flex-col items-center justify-center mt-12 text-center">
                <img
                    src={EMPTY_IMAGES[currentIndex]}
                    alt="Sem produtos"
                    className="w-72 h-72 md:w-96 md:h-96 rounded-2xl shadow-xl object-contain mb-6 transition-opacity duration-700 ease-in-out"
                />
                <h2 className="text-2xl font-semibold">
                    {favorites ? 'Nenhum favorito encontrado' : 'Nenhum show encontrado'}
                </h2>
                <p className="text-gray-600 mt-2">
                    {favorites
                        ? 'Você ainda não adicionou shows aos seus favoritos.'
                        : 'Você ainda não cadastrou nenhum show. Clique em "Show" e comece agora!'}
                </p>
            </div>
        );
    }

    return (
        <>
            {products.map((product) => (
                <ProductListItem
                    key={product.id}
                    product={product}
                    markFavorite={(is_favorite: boolean) => markFavorite(product.id, is_favorite)}
                    isManageMode={isManageMode}
                />
            ))}
        </>
    );
};

export default ProductList;
